import React, { useEffect, useState } from 'react';
import { ILxsCreateOrderResources } from '../lxs-create-order.props.autogenerated';
import { ILxsOrderData, IOrderHeader } from '../lxs-create-order.data';
import { toast } from 'sonner';
import AddressInformation from '../components/address-information';
import { ICoreContext } from '@msdyn365-commerce/core';
import OrderItems from '../components/items-table';
import { IShipToTableData } from '../../../actions/get-ship-tos.action';
import {
    convertSapToHtmlLinebreaks,
    convertSapToNewlineLinebreaks,
    convertNewlineToSapLinebreaks,
    convertHtmlToPlainText
} from '../../../utilities';
import { NO_DATA_FALLBACK, CREATE_ORDER_ADDITIONAL_INFORMATION_TEXT_LIMIT } from '../../../configuration';

export const OrderHeaderInfo = ({
    context,
    resources,
    orderData,
    step,
    productsHaveEndcustomers,
    pushHistoryState,
    setOrderHeader,
    setStep
}: {
    context: ICoreContext;
    orderData: ILxsOrderData;
    resources: ILxsCreateOrderResources;
    step: number;
    productsHaveEndcustomers: boolean;
    pushHistoryState: (currentStep: number) => void;
    setStep?: React.Dispatch<React.SetStateAction<number>>;
    setOrderHeader?: (orderHeader: IOrderHeader) => void;
}) => {
    const [additionalInfoValue, setAdditionalInfoValue] = useState<string>('');
    const [customerPoNumberValue, setCustomerPoNumberValue] = useState<string>('');

    useEffect(() => {
        const { AdditionalInfo, CustomerPONumber } = orderData.orderHeader || {};
        AdditionalInfo && setAdditionalInfoValue(convertSapToNewlineLinebreaks(AdditionalInfo));
        CustomerPONumber && setCustomerPoNumberValue(CustomerPONumber);
    }, []);

    const showToast = (message: string) => {
        toast.info(
            <div className='toast' role='alert'>
                <div className='toast-header'>
                    <h4>{resources.createOrderOrderHeadingInfoToastHeading}</h4>
                    <button className='toast-close' onClick={() => toast.dismiss()}></button>
                </div>
                <div className='toast-body'>
                    <span>{message}</span>
                </div>
            </div>
        );
    };

    const handleChange = <T extends HTMLInputElement | HTMLTextAreaElement>(
        event: React.ChangeEvent<T>,
        setValue: React.Dispatch<React.SetStateAction<string>>
    ) => {
        const inputValue = event.target instanceof HTMLTextAreaElement ? event.target.value : event.target.value.trim();
        setValue(inputValue);
    };

    const sanitizeAndUpdate = (
        inputValue: string,
        convertHtmlToPlainText: (input: string) => string,
        setInputValue: React.Dispatch<React.SetStateAction<string>>,
        updateOrderHeader: (sanitizedValue: string) => void,
        showToast: (message: string) => void,
        resources: ILxsCreateOrderResources
    ) => {
        const originalValue = inputValue;
        const sanitizedValue = convertHtmlToPlainText(inputValue);
        setInputValue(sanitizedValue);
        updateOrderHeader(sanitizedValue);

        if (originalValue !== '' && originalValue !== sanitizedValue) {
            showToast(resources.createOrderOrderHeadingInfoSanitizeText);
        }
    };

    const handleBlur = (
        event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
        setInputValue: React.Dispatch<React.SetStateAction<string>>,
        updateOrderHeader: (sanitizedValue: string) => void
    ) => {
        sanitizeAndUpdate(event.target.value, convertHtmlToPlainText, setInputValue, updateOrderHeader, showToast, resources);
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (customerPoNumberValue.trim() !== '') {
            setStep?.(5);
        }
    };

    return (
        <div className='row'>
            <div className='col-12 col-lg-8'>
                <form id='order-header-info' onSubmit={handleSubmit}>
                    <div className='msc-section-box lxs-create-order__header-info-section'>
                        <h4 className='msc-section-box__title'>{resources.createOrderGeneralOrderHeading}</h4>
                        <div className='row mb-4'>
                            <div className='col-md-6 mb-2'>
                                <label>{resources.createOrderCustomerPoLabel}</label>
                                {step === 5 ? (
                                    <p className='lxs-create-order__header-info-customer-po'>
                                        {orderData.orderHeader?.CustomerPONumber ?? NO_DATA_FALLBACK}
                                    </p>
                                ) : (
                                    <input
                                        type='text'
                                        id='customerPoNumber'
                                        autoFocus
                                        value={customerPoNumberValue}
                                        onBlur={event =>
                                            handleBlur(event, setCustomerPoNumberValue, sanitizedValue =>
                                                setOrderHeader?.({ ...orderData.orderHeader, CustomerPONumber: sanitizedValue })
                                            )
                                        }
                                        required
                                        onChange={event => handleChange(event, setCustomerPoNumberValue)}
                                        placeholder={resources.createOrderCustomerPoPlaceholder}
                                    ></input>
                                )}
                            </div>
                            <div className='col-md-6 mb-2'>
                                <label>{resources.createOrderSalesOrgLabel}</label>
                                <p>{orderData.orderHeader?.SalesOrgText ?? NO_DATA_FALLBACK}</p>
                            </div>
                            <div className='col-md-6 mb-2'>
                                <label>{resources.createOrderBusinessUnitLabel}</label>
                                <p>{orderData.orderHeader?.DivisionText ?? NO_DATA_FALLBACK}</p>
                            </div>
                            <div className='col-md-6 mb-2'>
                                <label>{resources.createOrderIncoterms1Label}</label>
                                <p>{orderData.orderHeader?.Incoterm1 ?? NO_DATA_FALLBACK}</p>
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12'>
                                <label>{resources.createOrderAdditionalInfoLabel}</label>
                                {step === 5 ? (
                                    orderData.orderHeader?.AdditionalInfo ? (
                                        <p
                                            className='lxs-create-order__text-auto-hyphens'
                                            dangerouslySetInnerHTML={{
                                                __html: convertSapToHtmlLinebreaks(orderData.orderHeader?.AdditionalInfo)
                                            }}
                                        ></p>
                                    ) : (
                                        <p>{NO_DATA_FALLBACK}</p>
                                    )
                                ) : (
                                    <textarea
                                        id='additionalInfo'
                                        value={additionalInfoValue}
                                        maxLength={CREATE_ORDER_ADDITIONAL_INFORMATION_TEXT_LIMIT}
                                        onBlur={event =>
                                            handleBlur(event, setAdditionalInfoValue, sanitizedValue =>
                                                setOrderHeader?.({
                                                    ...orderData.orderHeader,
                                                    AdditionalInfo: convertNewlineToSapLinebreaks(sanitizedValue)
                                                })
                                            )
                                        }
                                        onChange={event => handleChange(event, setAdditionalInfoValue)}
                                        placeholder={resources.createOrderMessagePlaceholderLabel}
                                        rows={6}
                                    ></textarea>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <div className='col-12 col-lg-4 pl-lg-0'>
                <AddressInformation soldTo={orderData.soldTo} shipTo={orderData.shipTo ?? ({} as IShipToTableData)} resources={resources} />
            </div>
            <div className='col-12'>
                <OrderItems
                    context={context}
                    itemsData={orderData}
                    resources={resources}
                    productsHaveEndcustomers={productsHaveEndcustomers}
                />
            </div>
            {step === 4 && (
                <div className='msc-form__button-group lxs-create-order__direction-button-wrapper col-12'>
                    <button
                        className='msc-cta__secondary'
                        type='button'
                        onClick={() => {
                            pushHistoryState(step - 1);
                            setStep?.(step - 1);
                        }}
                    >
                        {resources.createOrderPreviousPageButton}
                    </button>
                    <button className='msc-cta__primary' type='submit' form='order-header-info'>
                        {resources.createOrderNextPageButton}
                    </button>
                </div>
            )}
        </div>
    );
};
